import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { MOCK_HEADER_SETTINGS } from './useWebsiteHeaderSettings.mocks';
import { AlohaWebsiteHeaderSettings, BrannanWebsiteHeaderSettings, SupportedAlohaHeaderPhotoLayout, SupportedBrannanHeaderPhotoLayout } from './useWebsiteHeaderSettings.types';
import { useGuestSiteWebsiteHeaderSettingsQuery } from '@graphql/generated';
import useWebsiteHeaderUpdatesListener from '../useWebsiteHeaderUpdatesListener/useSubscribeWebsiteHeaderUpdates';

type StubArgs =
  | {
      websiteLayout: 'aloha';
      presentationLayout: SupportedAlohaHeaderPhotoLayout;
    }
  | {
      websiteLayout: 'brannan';
      presentationLayout: SupportedBrannanHeaderPhotoLayout;
    };

type UseWebsiteHeaderSettingsArgs = StubArgs & {
  eventHandle: string;
  /**
   * Only intended for use in local develoment.
   */
  forceDisable?: boolean;
};

/**
 * Stubbed hook to return website header settings based on the provided layout and presentation type.
 *
 *  @todo
 * - Add schema validation https://withjoy.atlassian.net/browse/PRNT-2869
 */
export function useWebsiteHeaderSettings(
  args: StubArgs & { eventHandle: string; forceDisable: true }
): {
  isLoading: boolean;
  websiteHeader: null;
};
export function useWebsiteHeaderSettings(args: { websiteLayout: 'aloha'; presentationLayout: 'center' | 'photoSlider'; eventHandle: string }): AlohaWebsiteHeaderSettings;
export function useWebsiteHeaderSettings(args: {
  websiteLayout: 'brannan';
  presentationLayout: 'center' | 'fullWidth' | 'pageWidth' | 'photoSlider' | 'photoTrio';
  eventHandle: string;
}): BrannanWebsiteHeaderSettings;
export function useWebsiteHeaderSettings(args: UseWebsiteHeaderSettingsArgs) {
  const { websiteLayout, presentationLayout, forceDisable } = args;
  const [isFeatureEnabled] = useIsFeatureEnabled('websiteHeaderConfigEnabled');

  const shouldSkip = forceDisable || !isFeatureEnabled;

  const { data, loading: isFetchingWebsiteHeaderSettings, refetch } = useGuestSiteWebsiteHeaderSettingsQuery({
    skip: shouldSkip,
    variables: { eventHandle: args.eventHandle },
    batchMode: 'off'
  });
  useWebsiteHeaderUpdatesListener(refetch);

  if (shouldSkip) {
    return {
      isLoading: false,
      websiteHeader: null
    };
  }

  const eventData = data?.eventByName;

  // Add this back once API is ready
  // if (!isFetchingWebsiteHeaderSettings && !eventData?.eventDesign?.websiteHeaderPresentationLayout) {
  //  return {
  //    isLoading: false,
  //    websiteHeader: null
  //  };
  // }

  // Need to do an explicit if/else, TSC has difficulty with union type discrimination in this case
  if (websiteLayout === 'aloha') {
    return {
      websiteHeader: {
        eventDisplayName: eventData?.info.eventDisplayName,
        greetingMessage: eventData?.info.greeting,
        headerPhoto: MOCK_HEADER_SETTINGS.aloha[presentationLayout]
      },

      isLoading: isFetchingWebsiteHeaderSettings
    };
  } else {
    return {
      websiteHeader: {
        eventDisplayName: eventData?.info.eventDisplayName,
        headerPhoto: MOCK_HEADER_SETTINGS.brannan[presentationLayout]
      },

      isLoading: isFetchingWebsiteHeaderSettings
    };
  }
}
